import React, { useState, useEffect } from 'react';
import './Sudoku.css';

// Helper: generate puzzle & solution
const generateBoard = (difficulty) => {
  const easy = 40, medium = 30, hard = 20;
  const clues = difficulty === 'easy' ? easy : difficulty === 'medium' ? medium : hard;

  const board = Array(9).fill(0).map(() => Array(9).fill(null));
  solveSudoku(board);

  const solution = board.map(row => row.slice());
  const puzzle = board.map(row => row.slice());

  let cellsToRemove = 81 - clues;
  while (cellsToRemove > 0) {
    const r = Math.floor(Math.random() * 9);
    const c = Math.floor(Math.random() * 9);
    if (puzzle[r][c] !== null) {
      puzzle[r][c] = null;
      cellsToRemove--;
    }
  }

  return { puzzle, solution };
};

// Backtracking solver
const solveSudoku = (board) => {
  const findEmpty = () => {
    for (let r = 0; r < 9; r++)
      for (let c = 0; c < 9; c++)
        if (!board[r][c]) return [r, c];
    return null;
  };

  const isValid = (num, [r, c]) => {
    for (let i = 0; i < 9; i++) {
      if (board[r][i] === num && i !== c) return false;
      if (board[i][c] === num && i !== r) return false;
    }

    const boxRow = Math.floor(r / 3) * 3;
    const boxCol = Math.floor(c / 3) * 3;
    for (let i = 0; i < 3; i++)
      for (let j = 0; j < 3; j++)
        if (board[boxRow + i][boxCol + j] === num && (boxRow + i !== r || boxCol + j !== c)) return false;

    return true;
  };

  const empty = findEmpty();
  if (!empty) return true;

  for (let num = 1; num <= 9; num++) {
    if (isValid(num, empty)) {
      board[empty[0]][empty[1]] = num;
      if (solveSudoku(board)) return true;
      board[empty[0]][empty[1]] = null;
    }
  }

  return false;
};

const Sudoku = () => {
  const [difficulty, setDifficulty] = useState('easy');
  const [puzzle, setPuzzle] = useState([]);
  const [solution, setSolution] = useState([]);
  const [userInput, setUserInput] = useState([]);
  const [status, setStatus] = useState('Good luck!');

  useEffect(() => {
    const { puzzle, solution } = generateBoard(difficulty);
    setPuzzle(puzzle);
    setSolution(solution);
    setUserInput(puzzle.map(row => row.slice()));
    setStatus('Good luck!');
  }, [difficulty]);

  const handleChange = (r, c, val) => {
    if (!puzzle[r][c]) {
      const updated = userInput.map(row => row.slice());
      const num = parseInt(val) || null;
      if (num === null || (num >= 1 && num <= 9)) updated[r][c] = num;
      setUserInput(updated);
    }
  };

  const isBoardComplete = () => {
    if (!userInput.length) return false;
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (!userInput[r][c]) return false;
      }
    }
    return true;
  };

  const checkSolution = () => {
    let correct = true;
    for (let r = 0; r < 9; r++) {
      for (let c = 0; c < 9; c++) {
        if (userInput[r][c] !== solution[r][c]) {
          correct = false;
          break;
        }
      }
    }
    setStatus(correct ? '🎉 Correct! You solved it!' : '❌ Not correct yet. Keep trying!');
  };

  if (!userInput.length || !puzzle.length || !solution.length) {
    return <div className="sudoku"><p>Loading puzzle…</p></div>;
  }

  return (
    <div className="sudoku">
      <h2>Sudoku</h2>
      <p>{status}</p>

      <div className="difficulty">
        <label>Difficulty: </label>
        <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)}>
          <option value="easy">Easy</option>
          <option value="medium">Medium</option>
          <option value="hard">Hard</option>
        </select>
      </div>

      <div className="sudoku-board">
        {userInput.map((row, r) => (
          <div className="sudoku-row" key={r}>
            {row.map((cell, c) => (
              <input
                key={`${r}-${c}`}
                type="text"
                maxLength="1"
                value={cell || ''}
                disabled={puzzle[r][c] !== null}
                className={`sudoku-cell ${puzzle[r][c] !== null ? 'prefilled' : ''} ${((r+1)%3===0 && r!==8) ? 'bottom-border' : ''} ${((c+1)%3===0 && c!==8) ? 'right-border' : ''}`}
                onChange={(e) => handleChange(r, c, e.target.value)}
              />
            ))}
          </div>
        ))}
      </div>

      <button
        className="reset-btn"
        onClick={checkSolution}
        disabled={!isBoardComplete()}
      >
        Check
      </button>
    </div>
  );
};

export default Sudoku;
